import type { OpenWork } from '@greco-gls/shared';
import { useQuery } from '@tanstack/react-query';

import { fetchAttention } from '@/api/endpoints';
import { queryKeys } from '@/api/queryClient';

/**
 * How often the bell asks again on its own. Auto-publish, a running build reporting missing keys
 * and another project filing a suggestion all happen without anything in this tab being written,
 * so no invalidation would ever catch them.
 */
const REFRESH_MS = 2 * 60_000;

export interface AttentionItem {
  kind: 'suggestion' | 'release' | 'missing';
  /** Where the work is done. */
  to: string;
  label: string;
  detail: string;
  count: number;
}

export interface Attention {
  items: AttentionItem[];
  /** The number of ITEMS — what the bell's badge shows, so it always matches the list under it. */
  total: number;
  isPending: boolean;
  /** The server has answered at least once, so `items` is the real list and not a placeholder. */
  isLoaded: boolean;
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** The wording and the link for one server row; the server sends counts only. */
export function toAttentionItem(work: OpenWork): AttentionItem {
  const base = `/${work.scopeType}s/${work.code}`;
  const { code, count, kind } = work;
  switch (kind) {
    case 'release':
      return {
        kind,
        to: `${base}/releases`,
        label: `${code}: ${plural(count, 'failed release')}`,
        detail: work.detail ?? 'Publishing did not finish.',
        count,
      };
    case 'missing':
      return {
        kind,
        to: `${base}/missing`,
        label: `${code}: ${plural(count, 'missing key')} reported`,
        detail: 'A running build asked for a key that does not exist yet.',
        count,
      };
    case 'suggestion':
      return {
        kind,
        to: `${base}/suggestions`,
        label: `${code}: ${plural(count, 'open suggestion')}`,
        detail:
          work.scopeType === 'package'
            ? 'A project proposed a change to a key this package owns.'
            : 'Another project proposed a change to a key it links from this one.',
        count,
      };
  }
}

const toAttentionItems = (rows: OpenWork[]) => rows.map(toAttentionItem);

/** What the bell has marked read: an item's identity → the count it had when it was read. */
export type AttentionReadMarks = Record<string, number>;

/** One signal per kind and owner, and `to` already names the owner. */
export const attentionId = (item: AttentionItem): string => `${item.kind}:${item.to}`;

/**
 * Whether the bell still has news about an item. A mark covers the count it was made at, so one
 * more failed release or reported key than the user has seen makes the item unread again.
 */
export const isAttentionUnread = (item: AttentionItem, marks: AttentionReadMarks): boolean =>
  item.count > (marks[attentionId(item)] ?? 0);

/**
 * Drops the marks of items that are gone, or `null` when none is. Without this, an owner whose 59
 * failures were read, then resolved, would hide its next single failure: 1 is not above 59.
 */
export function pruneAttentionReadMarks(
  marks: AttentionReadMarks,
  items: AttentionItem[],
): AttentionReadMarks | null {
  const live = new Set(items.map(attentionId));
  const kept = Object.entries(marks).filter(([id]) => live.has(id));
  return kept.length === Object.keys(marks).length ? null : Object.fromEntries(kept);
}

/**
 * What is waiting for somebody, across every scope the user can reach (spec §9.2, §9.3).
 *
 * One hook for both the bell and the dashboard panel, so the badge and the list can never disagree,
 * and one request behind it: `GET /attention` counts per project and package on the server. It used
 * to be four list reads per project and two per package, which for an admin was well over a hundred
 * requests on every page.
 *
 * Only signals somebody has to act on:
 *  - a suggestion nobody has decided — for a package key, or for a project key another project
 *    links (§7.7, ADR 0006);
 *  - a release that failed, because until a later one of the same target succeeds production is
 *    serving the previous one;
 *  - missing keys a running application reported and nobody has turned into keys yet (§7.12).
 */
export function useAttention(): Attention {
  const query = useQuery({
    queryKey: queryKeys.attention,
    queryFn: fetchAttention,
    select: toAttentionItems,
    refetchInterval: REFRESH_MS,
  });

  const items = query.data ?? [];
  return {
    items,
    // Items, not the sum of their counts: three reported keys are one thing to deal with, and a
    // badge reading 5 over a list of 2 rows reads as a bug.
    total: items.length,
    isPending: query.isPending,
    isLoaded: query.data !== undefined,
  };
}
